import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import MultiSelect from '../../components/MultiSelect';

const Itinerary = () => {
  const { locationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [activeTab, setActiveTab] = useState('days');
  const [allPackages, setAllPackages] = useState([]);
  const [formData, setFormData] = useState({
    id: '',
    location: locationId,
    days: [{ title: '', description: '' }],
    howToReach: { byAir: '', byTrain: '', byRoad: '' },
    faq: [{ question: '', answer: '' }],
    recentFaq: [{ question: '', answer: '' }],
    metaTitle: '',
    metaKeyword: '',
    metaDescription: '',
    keywordLink: [{ keyword: '', link: '' }],
    packages: []
  });

  useEffect(() => {
    fetchItinerary();
  }, []);

  const fetchItinerary = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`getItinerary/${locationId}`);
      
      if (response.status && response.data) {
        const data = response.data;
        setLocationName(data.location?.name || '');
        
        const allPkgs = (data.activePackages || []).map(p => ({
          ...p,
          name: p.vendorId?.business_name ? `${p.name} (${p.vendorId.business_name})` : p.name
        }));
        setAllPackages(allPkgs);

        setFormData({
          id: data._id || '',
          location: data.location?._id || locationId,
          days: Array.isArray(data.days) && data.days.length > 0 
            ? data.days.map(d => ({ title: d.title || '', description: d.description || '' }))
            : [{ title: '', description: '' }],
          howToReach: {
            byAir: data.howToReach?.byAir || '',
            byTrain: data.howToReach?.byTrain || '',
            byRoad: data.howToReach?.byRoad || ''
          },
          faq: Array.isArray(data.faq) && data.faq.length > 0 
            ? data.faq.map(f => ({ question: f.question || '', answer: f.answer || '' }))
            : [{ question: '', answer: '' }],
          recentFaq: Array.isArray(data.recentFaq) && data.recentFaq.length > 0 
            ? data.recentFaq.map(f => ({ question: f.question || '', answer: f.answer || '' }))
            : [{ question: '', answer: '' }],
          metaTitle: data.metaTitle || '',
          metaKeyword: data.metaKeyword || '',
          metaDescription: data.metaDescription || '',
          keywordLink: Array.isArray(data.keywordLink) && data.keywordLink.length > 0 
            ? data.keywordLink.map(k => ({ keyword: k.keyword || '', link: k.link || '' }))
            : [{ keyword: '', link: '' }],
          packages: Array.isArray(data.packages)
            ? data.packages.map(p => typeof p === 'object' ? p._id : p)
            : []
        });
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const filteredKeywordLinks = formData.keywordLink.filter(k => k.keyword?.trim() || k.link?.trim());
      const deleteAllKeywordLink = filteredKeywordLinks.length === 0 ? '1' : '0';

      const payload = {
        ...formData,
        keywordLink: filteredKeywordLinks,
        delete_all_keywordLink: deleteAllKeywordLink
      };

      const response = await apiService.post('updateItinerary', payload);
      if (response.status) {
        toast.success(formData.id ? 'Updated successfully' : 'Created successfully');
        await fetchItinerary();
      } else {
        toast.error(response.message || 'Failed to save');
      }
    } catch (error) {
      toast.error('Something went wrong!');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const addDay = () => {
    setFormData({
      ...formData,
      days: [...formData.days, { title: '', description: '' }]
    });
  };

  const removeDay = (index) => {
    const newDays = formData.days.filter((_, i) => i !== index);
    setFormData({ ...formData, days: newDays });
  };

  const updateDay = (index, field, value) => {
    const newDays = [...formData.days];
    newDays[index][field] = value;
    setFormData({ ...formData, days: newDays });
  };

  const addFaq = () => {
    setFormData({
      ...formData,
      faq: [...formData.faq, { question: '', answer: '' }]
    });
  };

  const removeFaq = (index) => {
    const newFaq = formData.faq.filter((_, i) => i !== index);
    setFormData({ ...formData, faq: newFaq });
  };

  const updateFaq = (index, field, value) => {
    const newFaq = [...formData.faq];
    newFaq[index][field] = value;
    setFormData({ ...formData, faq: newFaq });
  };

  const addRecentFaq = () => {
    setFormData({
      ...formData,
      recentFaq: [...formData.recentFaq, { question: '', answer: '' }]
    });
  };

  const removeRecentFaq = (index) => {
    const newRecentFaq = formData.recentFaq.filter((_, i) => i !== index);
    setFormData({ ...formData, recentFaq: newRecentFaq });
  };

  const updateRecentFaq = (index, field, value) => {
    const newRecentFaq = [...formData.recentFaq];
    newRecentFaq[index][field] = value;
    setFormData({ ...formData, recentFaq: newRecentFaq });
  };

  const addKeywordLink = () => {
    setFormData({
      ...formData,
      keywordLink: [...formData.keywordLink, { keyword: '', link: '' }]
    });
  };

  const removeKeywordLink = (index) => {
    const newKw = formData.keywordLink.filter((_, i) => i !== index);
    setFormData({ ...formData, keywordLink: newKw });
  };

  const updateKeywordLink = (index, field, value) => {
    const newKw = [...formData.keywordLink];
    newKw[index][field] = value;
    setFormData({ ...formData, keywordLink: newKw });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#009ef7]"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  const tabs = [
    { id: 'days', label: 'Day-wise Plan', icon: 'bi-calendar-event' },
    { id: 'reach', label: 'How to Reach', icon: 'bi-geo-alt' },
    { id: 'faq', label: 'FAQ', icon: 'bi-question-circle' },
    { id: 'meta', label: 'SEO', icon: 'bi-search' },
    { id: 'keywordLink', label: 'Keyword Link', icon: 'bi-link-45deg' },
    { id: 'packages', label: 'Packages', icon: 'bi-box-seam' }
  ];

  return (
    <div className="p-0">
      <div className="flex items-center justify-between mb-6">
        <div>
          <nav className="text-sm flex items-center mb-2">
            <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <Link to="/view-location" className="text-gray-500 hover:text-gray-700">Locations</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Itinerary</span>
          </nav>
          {locationName && (
            <h1 className="text-xl font-bold text-gray-900 flex items-center">
              <i className="bi bi-map mr-2.5 text-[#009ef7]"></i>
              {locationName}
            </h1>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200 px-6 py-4 bg-gray-50/50 rounded-t-lg">
            <div className="flex flex-wrap gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-[#009ef7] text-white shadow-sm'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon}`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'days' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Day-wise Itinerary</h3>
                    <p className="text-xs text-gray-500">Plan your journey day by day</p>
                  </div>
                  <button
                    type="button"
                    onClick={addDay}
                    className="px-3.5 py-2 bg-[#009ef7] text-white text-xs rounded-lg hover:bg-[#007acc] flex items-center gap-1.5 shadow-sm font-semibold"
                  >
                    <i className="bi bi-plus-circle"></i>
                    Add Day
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.days.map((day, index) => (
                    <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative">
                      <div className="flex items-center justify-between mb-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-blue-50 text-[#009ef7] rounded-full flex items-center justify-center font-bold text-xs">
                            {index + 1}
                          </div>
                          <span className="font-semibold text-gray-800 text-sm">Day {index + 1}</span>
                        </div>
                        {formData.days.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDay(index)}
                            className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <i className="bi bi-trash text-base"></i>
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 gap-3">
                        <input
                          type="text"
                          value={day.title || ''}
                          onChange={(e) => updateDay(index, 'title', e.target.value)}
                          placeholder="e.g., Arrival & City Tour"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                        />
                        <textarea
                          value={day.description || ''}
                          onChange={(e) => updateDay(index, 'description', e.target.value)}
                          placeholder="Describe the day's activities..."
                          rows="3"
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reach' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-bold text-gray-900">How to Reach</h3>
                  <p className="text-xs text-gray-500">Transportation options to reach the destination</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* By Air */}
                  <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-sky-50 text-sky-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="bi bi-airplane text-base"></i>
                      </div>
                      <label className="text-sm font-semibold text-gray-800">By Air</label>
                    </div>
                    <textarea
                      value={formData.howToReach.byAir || ''}
                      onChange={(e) => setFormData({ ...formData, howToReach: { ...formData.howToReach, byAir: e.target.value } })}
                      placeholder="Nearest airports, flight details..."
                      rows="4"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                    />
                  </div>
                  
                  {/* By Train */}
                  <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-50 text-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="bi bi-train-front text-base"></i>
                      </div>
                      <label className="text-sm font-semibold text-gray-800">By Train</label>
                    </div>
                    <textarea
                      value={formData.howToReach.byTrain || ''}
                      onChange={(e) => setFormData({ ...formData, howToReach: { ...formData.howToReach, byTrain: e.target.value } })}
                      placeholder="Railway stations, train routes..."
                      rows="4"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                    />
                  </div>
                  
                  {/* By Road */}
                  <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-orange-50 text-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="bi bi-car-front text-base"></i>
                      </div>
                      <label className="text-sm font-semibold text-gray-800">By Road</label>
                    </div>
                    <textarea
                      value={formData.howToReach.byRoad || ''}
                      onChange={(e) => setFormData({ ...formData, howToReach: { ...formData.howToReach, byRoad: e.target.value } })}
                      placeholder="Bus routes, driving directions..."
                      rows="4"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="space-y-8">
                {/* General FAQs */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Frequently Asked Questions</h3>
                      <p className="text-xs text-gray-500">Common questions and answers</p>
                    </div>
                    <button
                      type="button"
                      onClick={addFaq}
                      className="px-3 py-1.5 bg-[#009ef7] text-white text-xs rounded-lg hover:bg-[#007acc] flex items-center gap-1.5 shadow-sm font-semibold"
                    >
                      <i className="bi bi-plus-circle"></i>
                      Add FAQ
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.faq.map((item, index) => (
                      <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2.5">
                          <span className="text-xs font-semibold text-[#009ef7] uppercase tracking-wider">FAQ Q#{index + 1}</span>
                          {formData.faq.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeFaq(index)}
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-2.5">
                          <input
                            type="text"
                            value={item.question || ''}
                            onChange={(e) => updateFaq(index, 'question', e.target.value)}
                            placeholder="Enter question..."
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                          />
                          <textarea
                            value={item.answer || ''}
                            onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                            placeholder="Enter answer..."
                            rows="2"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Recent FAQs */}
                <div className="space-y-4 border-t pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Recent FAQs</h3>
                      <p className="text-xs text-gray-500">Recently asked questions</p>
                    </div>
                    <button
                      type="button"
                      onClick={addRecentFaq}
                      className="px-3 py-1.5 bg-[#009ef7] text-white text-xs rounded-lg hover:bg-[#007acc] flex items-center gap-1.5 shadow-sm font-semibold"
                    >
                      <i className="bi bi-plus-circle"></i>
                      Add Recent FAQ
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.recentFaq.map((item, index) => (
                      <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-2.5">
                          <span className="text-xs font-semibold text-teal-600 uppercase tracking-wider">Recent FAQ Q#{index + 1}</span>
                          {formData.recentFaq.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRecentFaq(index)}
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-2.5">
                          <input
                            type="text"
                            value={item.question || ''}
                            onChange={(e) => updateRecentFaq(index, 'question', e.target.value)}
                            placeholder="Enter question..."
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                          />
                          <textarea
                            value={item.answer || ''}
                            onChange={(e) => updateRecentFaq(index, 'answer', e.target.value)}
                            placeholder="Enter answer..."
                            rows="2"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'meta' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-bold text-gray-900">SEO Meta Information</h3>
                  <p className="text-xs text-gray-500">Optimize for search engines</p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Title</label>
                    <input
                      type="text"
                      value={formData.metaTitle}
                      onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                      placeholder="SEO optimized title..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Description</label>
                    <textarea
                      value={formData.metaDescription}
                      onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                      placeholder="SEO optimized description..."
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Meta Keyword</label>
                    <textarea
                      value={formData.metaKeyword}
                      onChange={(e) => setFormData({ ...formData, metaKeyword: e.target.value })}
                      placeholder="keyword1, keyword2, keyword3"
                      rows="2"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white resize-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'keywordLink' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Keyword Links</h3>
                    <p className="text-xs text-gray-500">Manage target redirects for relevant keywords</p>
                  </div>
                  <button
                    type="button"
                    onClick={addKeywordLink}
                    className="px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600 flex items-center gap-2 shadow-sm font-semibold"
                  >
                    <i className="bi bi-plus-circle"></i>
                    Add Row
                  </button>
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keyword</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target Link / URL</th>
                        <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-20">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formData.keywordLink && formData.keywordLink.length > 0 ? (
                        formData.keywordLink.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-3">
                              <input
                                type="text"
                                value={item.keyword || ''}
                                onChange={(e) => updateKeywordLink(index, 'keyword', e.target.value)}
                                placeholder="e.g. Travel Guide"
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                              />
                            </td>
                            <td className="px-6 py-3">
                              <input
                                type="text"
                                value={item.link || ''}
                                onChange={(e) => updateKeywordLink(index, 'link', e.target.value)}
                                placeholder="e.g. https://example.com/guide"
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                              />
                            </td>
                            <td className="px-6 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => removeKeywordLink(index)}
                                className="w-9 h-9 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-6 py-8 text-center text-gray-400 text-sm">
                            No keyword links added yet. Click "Add Row" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'packages' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">Packages</h3>
                  <p className="text-xs text-gray-500 mb-4">Select packages to link to this itinerary</p>
                  <MultiSelect
                    options={allPackages}
                    value={formData.packages || []}
                    onChange={(value) => setFormData({ ...formData, packages: value })}
                    placeholder="Select packages"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex justify-between items-center sticky bottom-0">
          <Link
            to="/view-location"
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 no-underline font-medium text-sm transition-all"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-[#009ef7] text-white rounded-lg hover:bg-[#007acc] disabled:opacity-50 font-medium shadow-sm text-sm transition-all flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle"></i>
                Save Itinerary
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Itinerary;
